import React from "react";
import { Link, useParams } from "react-router-dom";
import { LIST_HEADER, LIST_ROW } from "@/components/devicePanel/panelLayout";
import { PanelListSkeleton } from "@/components/devicePanel/PanelSkeleton";
import { JointReviewLoadError } from "@/components/jointReview/JointReviewLoadError";
import { useJointReviewProject, usePointer } from "@/hooks/useJointReview";
import { useWorklistQuery } from "@/hooks/useLabelingQueries";
import { caseLabel, caseUrl, nextUndone, reviewStudies, sharesState } from "@/types/jointReview";

/** A joint review's studies in review order, each with its items' progress.
 * "Join" opens the item the other reader is on (not in a blind review's
 * independent phase, where nothing is shared); "Next item" opens the first item
 * not done. */
const JointReviewListPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const worklist = useWorklistQuery(projectId ?? "");
  const project = useJointReviewProject(projectId);
  const pointer = usePointer(projectId, sharesState(project.data));
  if (project.isLoading) return <PanelListSkeleton />;
  if (project.isError || !project.data)
    return <JointReviewLoadError error={project.error} onRetry={() => project.refetch()} />;
  const data = project.data;
  const next = nextUndone(data, null);
  const independent = data.phase === "independent";
  const shared = pointer.data && !pointer.data.set_by_me ? pointer.data : null;
  return (
    <div className="h-full overflow-auto text-fg">
      <div className={LIST_HEADER}>
        <Link to="/labeling-overview" className="text-xs text-fg-muted hover:text-fg">
          ← Labeling
        </Link>
        <h1 className="text-sm font-semibold">{worklist.data?.name ?? "Joint review"}</h1>
        <span data-testid="progress" className="font-mono text-xs tabular-nums text-fg-muted">
          {independent
            ? `${data.done_items} of ${data.total_items} called`
            : `${data.final_items} of ${data.total_items} final`}
        </span>
        <span className="ml-auto flex gap-2 text-xs">
          {shared && (
            <Link
              to={caseUrl(projectId!, shared.study_uid, shared.item_id)}
              className="rounded-xs border border-border-strong px-2 py-0.5 text-fg-muted hover:text-fg"
            >
              {data.observer ? "Join the readers" : "Join the other reader"}
            </Link>
          )}
          {next ? (
            <Link
              to={caseUrl(projectId!, next.study_uid, next.item_id)}
              className="rounded-xs border border-signal bg-signal px-2 py-0.5 text-fg"
            >
              Next item ›
            </Link>
          ) : (
            <span role="status" className="text-fg">
              {independent ? "✓ You have called every item." : "✓ Every item is final."}
            </span>
          )}
        </span>
      </div>
      <ul>
        {reviewStudies(data).map((study) => {
          const done = study.items.filter((item) => item.done).length;
          return (
            <li key={study.study_uid}>
              <Link
                to={caseUrl(projectId!, study.study_uid)}
                className={`${LIST_ROW} hover:bg-surface-2 ${done === study.items.length ? "opacity-60" : ""}`}
              >
                <span className="font-mono text-xs text-fg">
                  {caseLabel(data, study.study_uid)}
                </span>
                <span className="text-xs text-fg-muted">
                  {study.items.length} item{study.items.length === 1 ? "" : "s"}
                </span>
                <span className="font-mono text-xs tabular-nums text-fg-dim">
                  {done}/{study.items.length} {independent ? "called" : "final"}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default JointReviewListPage;
